import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Plus, Pencil, Trash2, Copy, ChevronRight, ChevronDown, ChevronLeft, Languages, GripVertical, Sparkles, Globe, Loader2, RotateCw } from "lucide-react";

const LANGS = { en: ["🇬🇧", "English"], es: ["🇪🇸", "Español"], fr: ["🇫🇷", "Français"] };

const tierClass = (tier) =>
  tier === "premium" ? "bg-amber-100 text-amber-800"
    : tier === "basic" ? "bg-blue-100 text-blue-800"
      : "bg-slate-200 text-slate-600";

const Chip = ({ children, className = "", testId }) => (
  <span data-testid={testId} className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded ${className}`}>
    {children}
  </span>
);

const isUntranslatedCopy = (row) =>
  /\[[A-Z]{2}\]\s*$/.test(row.title || "") || ["partial", "failed", "interrupted"].includes(row.translation?.status);

function TranslateMenu({ row, onTranslate, onTranslateInPlace }) {
  const others = Object.keys(LANGS).filter((l) => l !== (row.language || "").toLowerCase());
  const busy = ["queued", "running"].includes(row.translation?.status);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" disabled={busy} className="text-slate-600 hover:text-blue-800 h-8 px-2" aria-label="Translate to other languages"
          title="Translate: create a translated copy of this course, its lessons and quizzes" data-testid={`admin-course-translate-${row.id}`}>
          <Languages size={14} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        {isUntranslatedCopy(row) && (
          <>
            <DropdownMenuItem onClick={() => onTranslateInPlace(row)} className="font-semibold text-amber-900" data-testid={`admin-course-translate-inplace-${row.id}`}>
              <Sparkles size={13} className="mr-2" /> Translate this copy into {LANGS[row.language]?.[1] || row.language}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <div className="px-2 py-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Create translated copy</div>
        {others.map((l) => (
          <DropdownMenuItem key={l} onClick={() => onTranslate(row, [l])} data-testid={`admin-course-translate-${row.id}-${l}`}>
            <span className="mr-2">{LANGS[l][0]}</span>{LANGS[l][1]}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onTranslate(row, others)} className="font-semibold" data-testid={`admin-course-translate-${row.id}-all`}>
          <Globe size={13} className="mr-2" /> All other languages
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function TranslationStatus({ row, onRetry }) {
  const tr = row.translation;
  if (!tr) return row.translated_from ? <Chip className="bg-amber-50 text-amber-700" testId={`admin-course-translated-${row.id}`}>auto-translated</Chip> : null;
  if (["queued", "running"].includes(tr.status)) {
    return (
      <Chip className="bg-blue-50 text-blue-800 inline-flex items-center gap-1" testId={`admin-course-translating-${row.id}`}>
        <Loader2 size={10} className="animate-spin" /> Translating {tr.done || 0}/{tr.total || "…"}
      </Chip>
    );
  }
  if (tr.status === "done") return <Chip className="bg-emerald-50 text-emerald-700" testId={`admin-course-translated-${row.id}`}>auto-translated</Chip>;
  const failed = tr.failed || [];
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={`admin-course-translate-failed-${row.id}`}>
      <Chip className="bg-red-50 text-red-700" testId={`admin-course-translate-failed-count-${row.id}`}>
        {tr.status === "partial" ? `${failed.length} not translated` : tr.status === "interrupted" ? "translation interrupted" : "translation failed"}
      </Chip>
      <button type="button" onClick={() => onRetry(row)} data-testid={`admin-course-translate-retry-${row.id}`}
        title={failed.map((f) => `${f.kind}: ${f.title}`).join("\n")}
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 hover:text-red-800">
        <RotateCw size={11} /> Retry
      </button>
    </span>
  );
}

function LessonRow({ lesson: l, h }) {
  const isDragging = h.dragState?.id === l.id;
  return (
    <li
      draggable
      onDragStart={() => h.onDragStart(l)}
      onDragEnd={h.onDragEnd}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); h.onDrop(l); }}
      data-testid={`admin-lesson-row-${l.id}`}
      className={`flex items-center gap-3 py-1 px-2 rounded transition-colors cursor-grab active:cursor-grabbing ${isDragging ? "opacity-40 bg-blue-50" : "hover:bg-white"}`}
    >
      <GripVertical size={14} className="text-slate-300 shrink-0" aria-hidden="true" />
      <span className="w-8 shrink-0 text-[11px] font-mono text-slate-400">{l.order ?? "?"}</span>
      <span className="flex-1 min-w-0 break-words text-slate-800">{l.title || "(untitled)"}</span>
      <Chip className={tierClass(l.tier)}>{l.tier || "free"}</Chip>
      <button type="button" onClick={() => h.onEdit(l)} className="text-slate-400 hover:text-blue-800 p-1" aria-label="Edit lesson" title="Edit lesson" data-testid={`admin-lesson-edit-${l.id}`}>
        <Pencil size={12} />
      </button>
      <button type="button" onClick={() => h.onDuplicate(l)} className="text-slate-400 hover:text-emerald-700 p-1" aria-label="Duplicate lesson" title="Duplicate lesson" data-testid={`admin-lesson-duplicate-${l.id}`}>
        <Copy size={12} />
      </button>
      <button type="button" onClick={() => h.onDelete(l)} className="text-slate-400 hover:text-red-600 p-1" aria-label="Delete lesson" title="Delete lesson" data-testid={`admin-lesson-delete-${l.id}`}>
        <Trash2 size={12} />
      </button>
    </li>
  );
}

function ChapterGroups({ courseId, lessons, h, onChapterReorder }) {
  const chapters = useMemo(() => {
    const m = new Map();
    lessons.forEach((l) => {
      const n = l.chapter_number ?? 0;
      if (!m.has(n)) m.set(n, { n, title: l.chapter_title, tier: l.chapter_tier, lessons: [] });
      m.get(n).lessons.push(l);
    });
    return [...m.values()].sort((a, b) => a.n - b.n);
  }, [lessons]);
  const [open, setOpen] = useState(() => new Set(chapters.length === 1 ? [chapters[0].n] : []));
  const toggle = (n) => setOpen((p) => { const s = new Set(p); s.has(n) ? s.delete(n) : s.add(n); return s; });
  const [dragCh, setDragCh] = useState(null);
  const [overCh, setOverCh] = useState(null);
  const dropOn = (target) => {
    const from = dragCh;
    setDragCh(null); setOverCh(null);
    if (from == null || from === target) return;
    const nums = chapters.map((c) => c.n);
    nums.splice(nums.indexOf(from), 1);
    nums.splice(nums.indexOf(target) + (chapters.findIndex((c) => c.n === from) < chapters.findIndex((c) => c.n === target) ? 1 : 0), 0, from);
    setOpen(new Set());
    onChapterReorder(courseId, nums);
  };

  return (
    <div className="space-y-1.5">
      {chapters.map((c) => {
        const isOpen = open.has(c.n);
        return (
          <div
            key={c.n}
            onDragOver={(e) => { if (dragCh != null) { e.preventDefault(); setOverCh(c.n); } }}
            onDrop={(e) => { if (dragCh != null) { e.preventDefault(); e.stopPropagation(); dropOn(c.n); } }}
            data-testid={`admin-chapter-${courseId}-${c.n}`}
            className={`rounded-lg border bg-white transition-colors ${dragCh === c.n ? "opacity-40" : ""} ${overCh === c.n && dragCh !== c.n ? "border-blue-500 ring-2 ring-blue-500/20" : "border-slate-200"}`}
          >
            <div className="flex items-center">
            <span
              draggable
              onDragStart={(e) => { e.stopPropagation(); e.dataTransfer.effectAllowed = "move"; setDragCh(c.n); }}
              onDragEnd={() => { setDragCh(null); setOverCh(null); }}
              title="Drag to move this chapter"
              aria-label="Drag to move this chapter"
              data-testid={`admin-chapter-drag-${courseId}-${c.n}`}
              className="pl-2 py-2 text-slate-300 hover:text-slate-600 cursor-grab active:cursor-grabbing shrink-0"
            >
              <GripVertical size={14} />
            </span>
            <button
              type="button"
              onClick={() => toggle(c.n)}
              data-testid={`admin-chapter-toggle-${courseId}-${c.n}`}
              className="flex-1 min-w-0 flex items-center gap-2 px-2 py-2 text-left hover:bg-slate-50 rounded-lg transition-colors"
            >
              {isOpen ? <ChevronDown size={14} className="text-slate-400 shrink-0" /> : <ChevronRight size={14} className="text-slate-400 shrink-0" />}
              <span className="flex-1 min-w-0 text-sm font-semibold text-slate-800 break-words">
                Ch {c.n} · {c.title || "Untitled chapter"}
              </span>
              <span className="text-[11px] text-slate-500 shrink-0">{c.lessons.length} lesson{c.lessons.length === 1 ? "" : "s"}</span>
              {c.tier && <Chip className={tierClass(c.tier)}>{c.tier}</Chip>}
            </button>
            </div>
            {isOpen && (
              <ol className="space-y-1 text-sm px-2 pb-2" data-testid={`admin-chapter-lessons-${courseId}-${c.n}`}>
                {c.lessons.map((l) => <LessonRow key={l.id} lesson={l} h={h} />)}
              </ol>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function CourseCard({ row, kids, isOpen, onToggle, onTranslate, onTranslateInPlace, onEdit, onDuplicate, onDelete, onAddLesson, testIds, lessonHandlers, onChapterReorder }) {
  return (
    <div data-testid={`admin-course-card-${row.id}`} className="bg-white border border-slate-200 rounded-2xl hover:border-slate-300 transition-colors">
      <div className="flex items-start gap-3 px-4 py-3">
        <button type="button" data-testid={testIds.courseExpand(row.id)} onClick={() => onToggle(row.id)}
          aria-label={isOpen ? "Collapse lessons" : "Expand lessons"} className="mt-1 text-slate-400 hover:text-blue-800 transition-colors shrink-0">
          {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        {row.cover_image && (
          <img src={row.cover_image} alt="" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
            className="hidden sm:block w-20 h-12 rounded-md object-cover shrink-0 bg-slate-100" />
        )}
        <div className="flex-1 min-w-0">
          <button type="button" onClick={() => onToggle(row.id)} className="text-left font-semibold text-slate-900 break-words hover:text-blue-800 transition-colors" data-testid={`admin-course-title-${row.id}`}>
            {row.title || "(untitled)"}
          </button>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Chip className="bg-slate-100 text-slate-600">{row.language || "—"}</Chip>
            {row.level && <Chip className="bg-slate-100 text-slate-600">{row.level}</Chip>}
            <Chip className={tierClass(row.tier)}>{row.tier || "free"}</Chip>
            <Chip className="bg-slate-50 text-slate-500 border border-slate-200">order {row.order ?? "—"}</Chip>
            <Chip className="text-slate-400" testId={`admin-course-lesson-count-${row.id}`}>{kids.length} lesson{kids.length === 1 ? "" : "s"}</Chip>
            <TranslationStatus row={row} onRetry={onTranslateInPlace} />
          </div>
        </div>
        <div className="flex items-center shrink-0">
          <TranslateMenu row={row} onTranslate={onTranslate} onTranslateInPlace={onTranslateInPlace} />
          <Button data-testid={testIds.editBtn} variant="ghost" onClick={() => onEdit(row)} className="text-slate-600 hover:text-blue-800 h-8 px-2" aria-label="Edit"><Pencil size={14} /></Button>
          <Button data-testid={testIds.duplicateBtn} variant="ghost" onClick={() => onDuplicate(row)} className="text-slate-600 hover:text-emerald-700 h-8 px-2" aria-label="Duplicate" title="Duplicate — clone this row and edit"><Copy size={14} /></Button>
          <Button data-testid={testIds.deleteBtn} variant="ghost" onClick={() => onDelete(row.id)} className="text-slate-600 hover:text-red-600 h-8 px-2" aria-label="Delete"><Trash2 size={14} /></Button>
        </div>
      </div>
      {isOpen && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-3 rounded-b-2xl">
          {kids.length === 0
            ? <div className="text-xs text-slate-500 italic py-1">No lessons attached to this course yet.</div>
            : <ChapterGroups courseId={row.id} lessons={kids} h={lessonHandlers} onChapterReorder={onChapterReorder} />}
          <Button size="sm" variant="ghost" onClick={() => onAddLesson(row)} data-testid={`admin-course-add-lesson-${row.id}`}
            className="mt-2 h-7 gap-1.5 text-xs text-blue-800 hover:text-blue-900 hover:bg-blue-50">
            <Plus size={12} /> Add lesson
          </Button>
        </div>
      )}
    </div>
  );
}

export function AdminPager({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const btn = "inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-white hover:border-blue-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors";
  return (
    <nav data-testid="admin-content-pagination" aria-label="Pagination" className="flex items-center justify-between gap-4 pt-2">
      <button type="button" onClick={() => onChange(page - 1)} disabled={page === 1} className={btn} data-testid="admin-content-pagination-prev">
        <ChevronLeft size={14} /> Previous
      </button>
      <div className="flex items-center gap-1">
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
          <button key={n} type="button" onClick={() => onChange(n)} aria-current={n === page ? "page" : undefined}
            data-testid={`admin-content-pagination-page-${n}`}
            className={`min-w-9 h-9 px-3 text-sm font-semibold rounded-lg transition-colors ${n === page ? "bg-blue-800 text-white" : "text-slate-600 hover:bg-white hover:text-blue-800"}`}>
            {n}
          </button>
        ))}
      </div>
      <button type="button" onClick={() => onChange(page + 1)} disabled={page === totalPages} className={btn} data-testid="admin-content-pagination-next">
        Next <ChevronRight size={14} />
      </button>
    </nav>
  );
}
